/**
 * savedPromptsCopy — the saved-prompt library's chrome, in the viewer's
 * language (2026-09-17). House voice: lowercase, terse; "Prest0n" keeps its
 * capital where it appears. Chrome only — never content (D9).
 *
 * The prompts themselves are the PERSON'S OWN WORDS and are carried verbatim;
 * nothing in this file is ever written in Prest0n's voice.
 */
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import type { ThreadListLang } from '../../types/threadCategories';

export interface SavedPromptsCopy {
  /** The trigger, the heading, and the way out. */
  openLibrary: string;
  /**
   * The phone '+' menu (owner ruling 2026-09-17): one control carrying both
   * "bring something in" actions, so the trailing cluster stays at two and the
   * library stops being unreachable in a group thread.
   */
  plusMenu: string;
  plusAttach: string;
  heading: string;
  close: string;
  done: string;
  /** Finding one. */
  searchPlaceholder: string;
  clearSearch: string;
  noMatches: string;
  empty: string;
  emptyHint: string;
  recentlyUsed: string;
  noCategory: string;
  /** What a row says about itself. */
  blanksCount: (n: number) => string;
  /** Putting one into the draft. */
  insert: string;
  replaceDraft: string;
  addToEnd: string;
  draftWillBeReplaced: string;
  /** The fill-in step. */
  fillInHeading: (title: string) => string;
  fillInHint: string;
  back: string;
  /** Saving and editing. */
  saveDraftAsPrompt: string;
  saveMessageAsPrompt: string;
  newPrompt: string;
  editPrompt: string;
  nameLabel: string;
  namePlaceholder: string;
  bodyLabel: string;
  bodyPlaceholder: string;
  categoryLabel: string;
  newCategory: string;
  blanksLabel: string;
  blanksNone: string;
  blanksHint: string;
  save: string;
  saving: string;
  cancel: string;
  /** Removing one. */
  deletePrompt: string;
  deleteConfirm: (title: string) => string;
  deleteYes: string;
  deleteNo: string;
  /** When something goes wrong — failure copy, and honest about which half broke. */
  saveFailed: string;
  loadFailed: string;
  libraryFull: (max: number) => string;
  titleRequired: string;
  bodyRequired: string;
  bodyTooLong: (max: number) => string;
  tooManyBlanks: (max: number) => string;

  /** The workspace shelf (phase 2). */
  filterAll: string;
  filterMine: string;
  filterTeam: string;
  teamMark: string;
  sharedBy: (name: string) => string;
  teamEmpty: string;
  editPromptAction: string;
  saveCopyAction: string;
  copySaved: string;
  /** Drift: your copy is ahead of the one the workspace reads. */
  driftNote: string;
  publishChanges: string;
  /** Publishing, and taking it back out — each said with its real limit. */
  shareWithTeam: string;
  shareConfirm: string;
  unshareFromTeam: string;
  /** The line that must never overstate what unsharing undoes. */
  unshareConfirm: string;
  removeFromTeam: string;
  sharing: string;
}

const COPY: Record<ThreadListLang, SavedPromptsCopy> = {
  en: {
    openLibrary: 'saved prompts',
    plusMenu: 'add to this message',
    plusAttach: 'attach a file',
    heading: 'prompts',
    close: 'close',
    done: 'done',
    searchPlaceholder: 'search your prompts…',
    clearSearch: 'clear',
    noMatches: 'nothing matches',
    empty: 'nothing saved yet',
    emptyHint: 'keep a prompt you would type twice — it lands here',
    recentlyUsed: 'recently used',
    noCategory: 'no category',
    blanksCount: (n) => (n === 1 ? '1 blank' : `${n} blanks`),
    insert: 'use it',
    replaceDraft: 'replace draft',
    addToEnd: 'add to the end',
    draftWillBeReplaced: 'this replaces what is in your draft',
    fillInHeading: (title) => `fill in “${title}”`,
    fillInHint: 'leave one empty and its name stays in the text',
    back: 'back',
    saveDraftAsPrompt: 'save this draft as a prompt',
    saveMessageAsPrompt: 'save as a prompt',
    newPrompt: 'new prompt',
    editPrompt: 'edit',
    nameLabel: 'name',
    namePlaceholder: 'e.g. new matter intake',
    bodyLabel: 'the prompt',
    bodyPlaceholder: 'what you would type…',
    categoryLabel: 'category',
    newCategory: 'new category…',
    blanksLabel: 'blanks',
    blanksNone: 'none — wrap anything in {{ }} to be asked for it',
    blanksHint: 'wrap anything in {{ }} and you will be asked for it each time',
    save: 'save',
    saving: 'saving…',
    cancel: 'cancel',
    deletePrompt: 'delete',
    deleteConfirm: (title) => `delete “${title}”? this cannot be undone.`,
    deleteYes: 'delete it',
    deleteNo: 'keep it',
    saveFailed: 'could not save that — try again',
    loadFailed: 'could not load the whole library — showing what is here',
    libraryFull: (max) => `that is all ${max} — delete one to save another`,
    titleRequired: 'give it a name',
    bodyRequired: 'write the prompt',
    bodyTooLong: (max) => `that is longer than ${max} characters`,
    tooManyBlanks: (max) => `only the first ${max} blanks are asked for`,

    filterAll: 'all',
    filterMine: 'mine',
    filterTeam: 'team',
    teamMark: 'team',
    sharedBy: (name) => `shared by ${name}`,
    teamEmpty: 'your workspace has not published any prompts yet',
    editPromptAction: 'edit',
    saveCopyAction: 'save a copy',
    copySaved: 'saved to your prompts',
    driftNote: 'your copy has changes the team has not got',
    publishChanges: 'publish your changes',
    shareWithTeam: 'share with the workspace',
    shareConfirm: 'everyone here who can see the prompt library will be able to use it. your own copy stays yours.',
    unshareFromTeam: 'take out of the workspace',
    // Never overstate what this undoes: a copy somebody already made is theirs.
    unshareConfirm: 'nobody new can use it. anyone who already saved their own copy keeps it.',
    removeFromTeam: 'remove from the workspace',
    sharing: 'sharing…',
  },
  es: {
    openLibrary: 'prompts guardados',
    plusMenu: 'añadir a este mensaje',
    plusAttach: 'adjuntar un archivo',
    heading: 'prompts',
    close: 'cerrar',
    done: 'listo',
    searchPlaceholder: 'busca en tus prompts…',
    clearSearch: 'limpiar',
    noMatches: 'nada coincide',
    empty: 'nada guardado todavía',
    emptyHint: 'guarda un prompt que escribirías dos veces — aparece aquí',
    recentlyUsed: 'usados hace poco',
    noCategory: 'sin categoría',
    blanksCount: (n) => (n === 1 ? '1 hueco' : `${n} huecos`),
    insert: 'usarlo',
    replaceDraft: 'reemplazar el borrador',
    addToEnd: 'añadir al final',
    draftWillBeReplaced: 'esto reemplaza lo que hay en tu borrador',
    fillInHeading: (title) => `completa “${title}”`,
    fillInHint: 'si dejas uno vacío, su nombre se queda en el texto',
    back: 'atrás',
    saveDraftAsPrompt: 'guardar este borrador como prompt',
    saveMessageAsPrompt: 'guardar como prompt',
    newPrompt: 'nuevo prompt',
    editPrompt: 'editar',
    nameLabel: 'nombre',
    namePlaceholder: 'p. ej. entrada de caso nuevo',
    bodyLabel: 'el prompt',
    bodyPlaceholder: 'lo que escribirías…',
    categoryLabel: 'categoría',
    newCategory: 'nueva categoría…',
    blanksLabel: 'huecos',
    blanksNone: 'ninguno — envuelve algo en {{ }} para que te lo pregunte',
    blanksHint: 'envuelve algo en {{ }} y te lo preguntará cada vez',
    save: 'guardar',
    saving: 'guardando…',
    cancel: 'cancelar',
    deletePrompt: 'eliminar',
    deleteConfirm: (title) => `¿eliminar “${title}”? no se puede deshacer.`,
    deleteYes: 'eliminarlo',
    deleteNo: 'conservarlo',
    saveFailed: 'no se pudo guardar — inténtalo otra vez',
    loadFailed: 'no se pudo cargar toda la biblioteca — mostrando lo que hay',
    libraryFull: (max) => `esos son los ${max} — elimina uno para guardar otro`,
    titleRequired: 'ponle un nombre',
    bodyRequired: 'escribe el prompt',
    bodyTooLong: (max) => `eso supera los ${max} caracteres`,
    tooManyBlanks: (max) => `solo se preguntan los primeros ${max} huecos`,

    filterAll: 'todos',
    filterMine: 'míos',
    filterTeam: 'equipo',
    teamMark: 'equipo',
    sharedBy: (name) => `compartido por ${name}`,
    teamEmpty: 'tu espacio de trabajo aún no ha publicado ningún prompt',
    editPromptAction: 'editar',
    saveCopyAction: 'guardar una copia',
    copySaved: 'guardado en tus prompts',
    driftNote: 'tu copia tiene cambios que el equipo no tiene',
    publishChanges: 'publicar tus cambios',
    shareWithTeam: 'compartir con el espacio de trabajo',
    shareConfirm: 'todos aquí que puedan ver la biblioteca de prompts podrán usarlo. tu propia copia sigue siendo tuya.',
    unshareFromTeam: 'quitar del espacio de trabajo',
    unshareConfirm: 'nadie nuevo podrá usarlo. quien ya haya guardado su propia copia la conserva.',
    removeFromTeam: 'quitar del espacio de trabajo',
    sharing: 'compartiendo…',
  },
};

export function savedPromptsCopy(lang: ThreadListLang): SavedPromptsCopy {
  return COPY[lang] ?? COPY.en;
}

/** The viewer's language (the platform's UI-language resolver). */
export function resolveSavedPromptsLang(): ThreadListLang {
  return getUserUiLanguage() === 'es' ? 'es' : 'en';
}

export const SAVED_PROMPTS_COPY_KEYS = Object.keys(COPY.en) as Array<keyof SavedPromptsCopy>;
